import type { PolymorphicProps } from "@kobalte/core";
import {
  Tooltip,
  type TooltipContentProps,
  type TooltipPortalProps,
  type TooltipRootOptions,
  type TooltipTriggerProps,
} from "@kobalte/core/tooltip";
import { createPresence } from "@solid-primitives/presence";
import { mergeRefs } from "@solid-primitives/refs";
import {
  type JSX,
  Show,
  type Signal,
  type ValidComponent,
  createEffect,
  createSignal,
  onCleanup,
  splitProps,
} from "solid-js";
import { twMerge } from "tailwind-merge";
import { AnimateChildren } from "../animation/AnimateChildren";

export function StTooltip(
  props: {
    noArrow?: boolean;
    dontKeepOpenOnHover?: boolean;
    children: JSX.Element;
    content: JSX.Element;
    portalProps?: Omit<TooltipPortalProps, "children">;
    contentProps?: TooltipContentProps & JSX.HTMLAttributes<HTMLDivElement>;
  } & TooltipRootOptions,
) {
  const signal = props.onOpenChange ? ([() => props.open, props.onOpenChange] as Signal<boolean>) : createSignal(false);
  const [open, setOpen] = signal;

  const duration = 100;
  const openDelay = props.openDelay ?? 50;
  const closeDelay = props.closeDelay ?? 50;
  const presence = createPresence(open, {
    enterDuration: duration + openDelay,
    exitDuration: duration + closeDelay,
  });

  const [split, rest] = splitProps(props, ["content", "portalProps", "contentProps", "noArrow", "dontKeepOpenOnHover"]);

  return (
    <Tooltip
      openDelay={openDelay}
      closeDelay={closeDelay}
      onOpenChange={setOpen}
      open={open()}
      {...rest}
      forceMount={presence.isMounted()}
    >
      {rest.children}

      <Tooltip.Portal {...split.portalProps}>
        <AnimateChildren
          show={open()}
          duration={duration}
          presence={presence}
          enter={{
            scale: [0.9, 1],
            opacity: [0, 1],
            delay: openDelay,
          }}
          exit={{
            scale: 0.9,
            opacity: 0,
            delay: closeDelay,
          }}
        >
          {(anim) => (
            <Tooltip.Content
              {...split.contentProps}
              ref={anim.ref}
              class={twMerge(
                `text-sm px-2 py-1 origin-[var(--kb-tooltip-content-transform-origin)]
            rounded-lg shadow-xl bg-tile text-primary-text border border-primary-border z-[100]
            `,
                split.dontKeepOpenOnHover && "!pointer-events-none",
                split.contentProps?.class,
              )}
            >
              <Show when={!split.noArrow}>
                <Tooltip.Arrow />
              </Show>

              {split.content}
            </Tooltip.Content>
          )}
        </AnimateChildren>
      </Tooltip.Portal>
    </Tooltip>
  );
}

export const StTooltipTrigger: <T extends ValidComponent = "button">(
  props: PolymorphicProps<T, TooltipTriggerProps<T>> & { keepOpenOnClick?: boolean },
) => JSX.Element = (props) => {
  let ref!: HTMLElement;
  createEffect(() => {
    if (props.keepOpenOnClick) {
      const handler = (ev: Event) => {
        if (ref?.contains(ev.target as HTMLElement)) {
          ev.preventDefault();
          ev.stopImmediatePropagation();
        }
      };
      const clickHandler = (ev: MouseEvent) => {
        if (ref?.contains(ev.target as HTMLElement)) {
          ev.preventDefault();
          ev.stopImmediatePropagation();
          if (props.onClick && typeof props.onClick === "function") {
            // biome-ignore lint/suspicious/noExplicitAny: <explanation>
            props.onClick(ev as any);
          }
        }
      };
      window.addEventListener("pointerdown", handler, { capture: true });
      window.addEventListener("pointerup", handler, { capture: true });
      window.addEventListener("click", clickHandler, { capture: true });
      onCleanup(() => {
        window.removeEventListener("pointerdown", handler, { capture: true });
        window.removeEventListener("pointerup", handler, { capture: true });
        window.addEventListener("click", clickHandler, { capture: true });
      });
    }
  });

  return (
    <Tooltip.Trigger
      ref={mergeRefs(props.ref, (el) => {
        ref = el;
      })}
      {...props}
    />
  );
};
